import React, { useState, useEffect } from 'react';
import api from '../services/api';
import PlanSelectionModal from './PlanSelectionModal';

const PricingSection = ({ fadeElementsRef }) => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [selectedTier, setSelectedTier] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadPricingPlans();
  }, []);

  const loadPricingPlans = async () => {
    try {
      const response = await api.getPricingPlans();
      if (response.success) {
        setTiers(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load pricing plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (tier) => {
    setSelectedTier(tier);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedTier(null);
  };

  const getPrice = (tier) => {
    if (billingPeriod === 'yearly' && tier.price_yearly) {
      return tier.price_yearly;
    }
    return tier.price_monthly;
  };

  const formatPrice = (price, currency) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: currency || 'KES',
      minimumFractionDigits: 0,
    }).format(price).replace('KES', currency || 'Ksh');
  };

  if (loading) {
    return (
      <section id="pricing" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-gray-400">Loading pricing plans...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section id="pricing" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center fade-in" ref={el => fadeElementsRef.current.push(el)}>
            <h2 className="text-base font-semibold tracking-wider text-gold uppercase">Flexible Plans</h2>
            <p className="mt-2 text-3xl font-extrabold text-white sm:text-4xl">
              Scale as You Grow
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-400 mx-auto">
              Transparent pricing with no hidden fees
            </p>
          </div>

          <div className="mt-16 flex justify-center">
            <div className="inline-flex rounded-md shadow-sm">
              <button
                type="button"
                onClick={() => setBillingPeriod('monthly')}
                className={`relative inline-flex items-center px-6 py-3 rounded-l-md border border-gold text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-gold ${
                  billingPeriod === 'monthly'
                    ? 'bg-gold text-white'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingPeriod('yearly')}
                className={`relative inline-flex items-center px-6 py-3 rounded-r-md border border-gold border-l-0 text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-gold ${
                  billingPeriod === 'yearly'
                    ? 'bg-gold text-white'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                Yearly {tiers.some(t => t.price_yearly) && <span className="ml-1">(Save 20%)</span>}
              </button>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3">
            {tiers.map((tier, index) => {
              const features = typeof tier.features === 'string' 
                ? JSON.parse(tier.features) 
                : tier.features || [];
              
              const enabledApps = tier.apps?.filter(app => app.is_enabled) || [];
              
              return (
                <div
                  key={tier.id}
                  className={`pricing-card glass-card rounded-xl p-8 fade-in relative ${
                    tier.is_popular ? 'border-2 border-gold popular' : ''
                  }`}
                  style={{ transitionDelay: `${0.1 + index * 0.1}s` }}
                  ref={el => fadeElementsRef.current.push(el)}
                >
                  {tier.is_popular && (
                    <div className="absolute top-0 right-0 bg-gold text-black px-4 py-1 rounded-bl-lg rounded-tr-lg text-sm font-bold">
                      POPULAR
                    </div>
                  )}
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-white">{tier.name}</h3>
                    <p className="mt-2 text-gray-400">{tier.description}</p>
                  </div>
                  <div className="mb-8">
                    <span className="text-4xl font-bold text-white">
                      {formatPrice(getPrice(tier), tier.currency)}
                    </span>
                    <span className="text-gray-400">/{billingPeriod === 'yearly' ? 'year' : 'month'}</span>
                  </div>
                  <ul className="space-y-4 mb-8">
                    <li className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <i className="fas fa-check-circle text-gold"></i>
                      </div>
                      <div className="ml-3">
                        <p className="text-gray-300">
                          {tier.max_users === 999 ? 'Unlimited' : tier.max_users} Users
                        </p>
                      </div>
                    </li>
                    {features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <div className="flex-shrink-0 mt-1">
                          <i className="fas fa-check-circle text-gold"></i>
                        </div>
                        <div className="ml-3">
                          <p className="text-gray-300">{feature}</p>
                        </div>
                      </li>
                    ))}
                    {enabledApps.length > 0 && (
                      <li className="flex items-start">
                        <div className="flex-shrink-0 mt-1">
                          <i className="fas fa-check-circle text-gold"></i>
                        </div>
                        <div className="ml-3">
                          <p className="text-gray-300">
                            Access to {enabledApps.length} App{enabledApps.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </li>
                    )}
                  </ul>
                  <button
                    onClick={() => handleSelectPlan(tier)}
                    className={`w-full py-3 rounded-md font-medium ${
                      tier.is_popular ? 'btn-gold' : 'btn-ghost'
                    }`}
                  >
                    {tier.is_popular ? 'Start Free Trial' : 'Get Started'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {showModal && selectedTier && (
        <PlanSelectionModal
          tier={selectedTier}
          billingPeriod={billingPeriod}
          onClose={handleModalClose}
        />
      )}
    </>
  );
};

export default PricingSection;

